import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';

import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {firstValueFrom} from 'rxjs';
import {HrTool} from '@frontend/directus';
import {
  ParticipatingToolResponse,
  ProjectChapterType,
  ProjectStepType,
  TenderPhaseDetailsResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {InvitationTableComponent} from '../../../../../../shared/components/table/invitation-table/invitation-table.component';
import {
  InvitationTableTemplateContext,
  InviteToolsResponseOf,
  PhaseInvitationConfig,
  ProviderDecisionReason,
} from '../../../../../../shared/components/table/invitation-table/invitation-table.types';
import {RfdParticipatingTool} from './rfd-invitation.types';
import {InvitationDataLoaderService} from '../../../../../../shared/services/invitation-data-loader.service';
import {
  ProviderTotalScoreCalculationService,
  ProviderTotalScoreResult,
} from '../../../../../../shared/services/provider-total-score-calculation.service';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {PreviousStepIncompleteNoticeComponent} from '../../../../../../shared/components/previous-step-incomplete-notice/previous-step-incomplete-notice.component';
import {RfdScheduleTableComponent} from './components/rfd-schedule-table/rfd-schedule-table.component';
import {ResultScoreCellComponent} from '../../../../../../shared/components/result-score-cell/result-score-cell.component';
import {DecisionReasonModalComponent} from '../../../../../../shared/components/decision-reason-modal/decision-reason-modal.component';

@Component({
  selector: 'app-step-9-4-invitation-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    InvitationTableComponent,
    Button,
    Tooltip,
    PreviousStepIncompleteNoticeComponent,
    RfdScheduleTableComponent,
    ResultScoreCellComponent,
    DecisionReasonModalComponent,
  ],
  templateUrl: './step-7-4-invitation-list.component.html',
})
export class Step74InvitationListComponent
  extends ProjectBaseStepComponent
  implements AfterViewInit
{
  private readonly transloco = inject(TranslocoService);
  private readonly invitationDataLoader = inject(InvitationDataLoaderService);
  private readonly providerTotalScoreCalculationService = inject(
    ProviderTotalScoreCalculationService,
  );

  readonly totalResultTemplate =
    viewChild<TemplateRef<InvitationTableTemplateContext<RfdParticipatingTool>>>(
      'totalResultTemplate',
    );

  readonly reasonTemplate =
    viewChild<TemplateRef<InvitationTableTemplateContext<RfdParticipatingTool>>>('reasonTemplate');

  readonly invitationTable = viewChild(InvitationTableComponent);

  protected readonly currentPhase = signal<TenderPhaseDetailsResponse | null>(null);
  private readonly rfiProviderScores = signal<Map<string, ProviderTotalScoreResult>>(new Map());
  private readonly rfpProviderScores = signal<Map<string, ProviderTotalScoreResult>>(new Map());
  protected readonly rfiWeightSum = signal(0);
  protected readonly rfpWeightSum = signal(0);
  private readonly totalResultTemplateSignal = signal<TemplateRef<
    InvitationTableTemplateContext<RfdParticipatingTool>
  > | null>(null);
  private readonly reasonTemplateSignal = signal<TemplateRef<
    InvitationTableTemplateContext<RfdParticipatingTool>
  > | null>(null);

  protected readonly reasonModalVisible = signal(false);
  protected readonly selectedReasonTool = signal<RfdParticipatingTool | null>(null);

  private readonly RFP_COMPLETION_STEP: {chapterId: ProjectChapterType; stepId: ProjectStepType} = {
    chapterId: ProjectChapterType.RFP,
    stepId: ProjectStepType.RFP_PROVIDER_RESULTS,
  };

  protected openReasonModal(item: RfdParticipatingTool): void {
    this.selectedReasonTool.set(item);
    this.reasonModalVisible.set(true);
  }

  protected closeReasonModal(): void {
    this.reasonModalVisible.set(false);
    this.selectedReasonTool.set(null);
  }

  protected onReasonSaved(updatedReason: unknown): void {
    const reason = updatedReason as {
      participatingToolId?: number;
      mainReasonId?: string | null;
      detailReasonIds?: string[];
      generatedText?: string;
      comment?: string;
    };

    const tool = this.selectedReasonTool();
    if (tool && reason.mainReasonId) {
      const updatedDecisionReason: ProviderDecisionReason = {
        mainReasonId: reason.mainReasonId,
        detailReasonIds: reason.detailReasonIds ?? [],
        generatedText: reason.generatedText ?? '',
        comment: reason.comment ?? null,
        hasValue: true,
      };

      this.invitationTable()?.updateToolItemById(tool.id, {
        decisionReason: updatedDecisionReason,
      } as Partial<RfdParticipatingTool>);
    }

    this.closeReasonModal();
  }

  protected readonly isStepPrerequisiteCompleted = computed(() => {
    return this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFD, stepId: ProjectStepType.RFD_INVITATION_PREVIEW},
    ]);
  });

  protected readonly rfdPhaseConfig = computed((): PhaseInvitationConfig<RfdParticipatingTool> => ({
    phaseType: TenderPhaseType.RFD,
    timeFrameNumber: 4,
    tableConfig: {
      columns: [
        {
          field: 'toolProvider.name',
          header: 'project.steps.invitationList.provider',
          sortable: true,
          width: '20%',
        },
        {
          field: 'translatedName',
          header: 'project.steps.invitationList.tool',
          sortable: true,
          width: '20%',
        },
        {
          field: 'totalResult',
          header: 'project.steps.invitationList.totalResult',
          width: '30%',
          template: this.totalResultTemplateSignal() ?? undefined,
        },
        {
          field: 'selection',
          header: 'project.steps.invitationList.selection',
          sortable: true,
          sortField: 'selection',
          width: '12%',
        },
        {
          field: 'decisionReason',
          header: 'project.steps.invitationList.reason',
          width: '16%',
          template: this.reasonTemplateSignal() ?? undefined,
        },
        {
          field: 'status',
          header: 'project.steps.invitationList.status',
          sortable: true,
          sortField: 'status',
          width: '10%',
        },
      ],
      emptyMessage: 'project.steps.rfd.invitationList.noData',
    },
    timeframeConfig: {
      tooltipKeys: {
        plannedShippingDate: 'project.steps.rfd.invitationList.plannedShippingDate',
        plannedSubmissionDate: 'project.steps.rfd.invitationList.plannedSubmissionDate',
      },
    },
    prerequisiteChecker: () => this.prerequisitesMet(),
    stepLockedFn: () => this.isCurrentStepLocked(),
    dataLoader: (projectId) => this.loadRfdData(projectId),
    translationKeys: {
      title: 'project.steps.rfd.invitationList.title',
    },
  }));

  ngAfterViewInit(): void {
    const totalResultTemplate = this.totalResultTemplate();
    if (totalResultTemplate) {
      this.totalResultTemplateSignal.set(totalResultTemplate);
    }

    const reasonTemplate = this.reasonTemplate();
    if (reasonTemplate) {
      this.reasonTemplateSignal.set(reasonTemplate);
    }
  }

  onCurrentPhaseChange(phase: TenderPhaseDetailsResponse | null): void {
    this.currentPhase.set(phase);
  }

  private readonly prerequisitesMet = computed(() => {
    this.stepState.chaptersWithStatuses();

    const chaptersCompleted = [
      ProjectChapterType.PROJECT_SETUP,
      ProjectChapterType.PRINCIPLE_DEFINITION,
      ProjectChapterType.ACTUAL_SYSTEMS,
      ProjectChapterType.EVALUATION_CRITERIA,
      ProjectChapterType.RFI,
      ProjectChapterType.RFP,
    ].every((chapterId) => this.stepState.isChapterCompleted(chapterId));

    const specificStepsCompleted = this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFD, stepId: ProjectStepType.RFD_DEMONSTRATION_AGENDA},
      {chapterId: ProjectChapterType.RFD, stepId: ProjectStepType.RFD_DEMONSTRATION_USE_CASES},
      {chapterId: ProjectChapterType.RFD, stepId: ProjectStepType.RFD_INVITATION_PREVIEW},
    ]);

    return chaptersCompleted && specificStepsCompleted;
  });

  private async loadRfdData(
    projectId: number,
  ): Promise<InviteToolsResponseOf<RfdParticipatingTool>> {
    const [{directusHrTools, participatingToolsResponse}, rfiProviderScores, rfpProviderScores] =
      await Promise.all([
        firstValueFrom(
          this.invitationDataLoader.loadParticipatingToolsData(projectId, TenderPhaseType.RFD),
        ),
        this.providerTotalScoreCalculationService.calculateProviderTotalsWithWeight(
          projectId,
          TenderPhaseType.RFI,
        ),
        this.providerTotalScoreCalculationService.calculateProviderTotalsWithWeight(
          projectId,
          TenderPhaseType.RFP,
        ),
      ]);

    this.rfiProviderScores.set(rfiProviderScores.totals);
    this.rfpProviderScores.set(rfpProviderScores.totals);
    this.rfiWeightSum.set(rfiProviderScores.totalWeight);
    this.rfpWeightSum.set(rfpProviderScores.totalWeight);

    const toolItems = participatingToolsResponse.tools
      .map((rfdTool: ParticipatingToolResponse) => {
        const hrTool = directusHrTools.find((tool: HrTool) => tool.id === rfdTool.hrToolDirectusId);
        return hrTool ? this.mapToParticipatingTool(hrTool, rfdTool) : null;
      })
      .filter((tool: RfdParticipatingTool | null): tool is RfdParticipatingTool => tool !== null);

    return {
      tools: toolItems,
      currentPhase: participatingToolsResponse.currentPhase,
      maxAllowedParticipants: participatingToolsResponse.maxAllowedParticipants,
      calculatedPhaseSubmissionDate: participatingToolsResponse.calculatedPhaseSubmissionDate,
    };
  }

  private mapToParticipatingTool(
    tool: HrTool,
    rfdTool: ParticipatingToolResponse,
  ): RfdParticipatingTool {
    const lang = this.transloco.getActiveLang();
    const translation = tool.translations.find((t) => t.language === lang);
    const rfiScore = this.rfiProviderScores().get(tool.id.toString());
    const rfpScore = this.rfpProviderScores().get(tool.id.toString());

    const hasDecisionReason = !!rfdTool.rfdMainReasonUuid;

    return {
      ...tool,
      translatedName: (translation?.name?.trim() !== '' ? translation?.name : tool.name) ?? null,
      participatingToolId: rfdTool.id,
      selection: rfdTool.rfdSelection ?? null,
      status: rfdTool.rfdStatus,
      rfdDatetime: rfdTool.rfdDatetime ?? null,
      rfdPlace: rfdTool.rfdPlace ?? null,
      rfdAppointmentStatus: rfdTool.rfdAppointmentStatus ?? null,
      rfiPercentage: rfiScore?.percentage ?? null,
      rfpPercentage: rfpScore?.percentage ?? null,
      decisionReason: hasDecisionReason
        ? {
            mainReasonId: rfdTool.rfdMainReasonUuid ?? null,
            detailReasonIds: rfdTool.rfdDetailReasonUuids ?? [],
            generatedText: rfdTool.rfdReasonText ?? '',
            comment: rfdTool.rfdReasonComment ?? null,
            hasValue: true,
          }
        : null,
    };
  }

  navigateToRfp() {
    this.stepState.setActiveStep(
      this.RFP_COMPLETION_STEP.chapterId,
      this.RFP_COMPLETION_STEP.stepId,
    );
  }
}
